import { ServiceStore } from "@intrasync/stores";
import type ProjectDashboardStore from "./ProjectDashboard.Store";
import { RouteType } from "types/RouteType";

const projectDashboardRoute: RouteType[] = [
  {
    name: "Project Dashboard",
    path: "/job/:jobNumber/dashboard",
    layout: "/app",
    viewportCard: {
      header: {
        title: "Project Dashboard",
        component: () => import("./ProjectDashboard.Header"),
        enableToolbox: true,
        enableBackButton: true,
        enableRefreshButton: true,
      },
      body: {
        component: () => import("./ProjectDashboard.Body"),
        grayBackground: true,
      },
    },
    group: "Project Management",
    requiresAuth: true,
    requiresPermission: false,
    projectRoute: true,
    channels: [
      "job",
      "job-stats/trailer-loads/:jobNumber",
      "job-stats/ifc-status/:jobNumber",
      "job-stats/hardware-count/:jobNumber",
      "job-stats/casting-dates/:jobNumber",
      "job-stats/panel-count/:jobNumber",
      "job-attribute/:jobNumber",
    ],
    enableAutoFind: false,
    onEnter: async ({ params, rootStore }) => {
      // ProjectDashboard onEnter called

      const jobNumber = params?.jobNumber;
      if (!jobNumber) return;

      // Load project data

      await (rootStore.jobStore as ServiceStore).find({
        query: { jobNumber: jobNumber },
      });

      // Load related data

      // Match on jobNumber (the route param) via .entities.find() per CLAUDE.md — getById keys on the
      // `id` idField, not jobNumber, so it returned undefined and POs/stats/account never loaded.
      const project = ((rootStore.jobStore as ServiceStore).entities || []).find(
        (job) => (job as { jobNumber?: number }).jobNumber === Number(jobNumber)
      ) as { id: number; jobNumber: number; accountId?: number } | undefined;
      if (project) {
        // Load purchase orders for the project

        await (rootStore.purchaseOrdersStore as ServiceStore).find({
          query: {
            jobId: project.id,
            $limit: 500,
          },
        });

        // Load job stats
        const jobNumber = project.jobNumber;
        if (jobNumber) {
          await Promise.all([
            (rootStore.jobStatsPanelCountStore as ServiceStore)?.find({
              query: { jobNumber },
            }),

            (rootStore.jobStatsCastingDatesStore as ServiceStore)?.find({
              query: { jobNumber },
            }),

            (rootStore.jobStatsHardwareCountStore as ServiceStore)?.find({
              query: { jobNumber },
            }),

            (rootStore.jobStatsIfcStatusStore as ServiceStore)?.find({
              query: { jobNumber },
            }),

            (rootStore.jobStatsTrailerLoadsStore as ServiceStore)?.find({
              query: { jobNumber },
            }),

            // Load job schedule milestones
            (rootStore.jobScheduleStore as ServiceStore)?.find({
              query: {
                jobId: project.id,
                $limit: 100,
              },
            }),
          ]);
        }

        // Load account data if available
        if (project.accountId) {
          await (rootStore.accountsStore as ServiceStore).get(
            Number(project.accountId)
          );
        }
      }
    },
    onParamsChange: async ({
      rootStore,
      queryParams,
      previousQueryParams,
      params,
    }) => {
      // onParamsChange: refresh data only on real param changes (not dialog open/close)

      // Don't refresh on dialog changes
      if (
        queryParams?.dialogName ||
        (!queryParams?.dialogName && previousQueryParams?.dialogName)
      ) {
        return;
      }

      // Refresh data when project changes
      const jobNumber = params?.jobNumber;
      if (!jobNumber) return;

      const projectDashboardStore =
        rootStore?.projectDashboardStore as ProjectDashboardStore;
      if (projectDashboardStore) {
        await projectDashboardStore.refreshProjectData(jobNumber as number);
      }
    },
    dialogs: [
      {
        name: "Purchase Order Details",
        component: () =>
          import(
            "../../crm/Projects/ProjectDetails/ProjectDetailsDialogs/PurchaseOrderDetails.Dialog"
          ),
        title: "Purchase Order Details",
        height: 600,
        cancelButton: true,
      },
      {
        name: "JobScheduleList",
        component: () => import("../Dialogs/JobScheduleList"),
        title: "Project Schedule",
        onSubmit: async () => {
          // Dialog manages its own submission through the store
          return true;
        },
        onSubmitValidation: () => {
          // Validation handled internally by dialog
          return true;
        },
        cancelButton: false,
        height: 700,
      },
      {
        name: "JobScheduleView",
        component: () => import("../Dialogs/JobScheduleView"),
        title: "Milestone Details",
        onSubmit: async () => {
          // View-only dialog, no submission needed
          return true;
        },
        onSubmitValidation: () => {
          return true;
        },
        cancelButton: false,
        height: 600,
      },
      {
        name: "JobScheduleForm",
        component: () => import("../Dialogs/JobScheduleForm"),
        title: "Edit Milestone",
        onSubmit: async () => {
          // Form manages its own submission through the store
          return true;
        },
        onSubmitValidation: () => {
          // Validation handled internally by form
          return true;
        },
        cancelButton: true,
        height: 650,
      },
    ],
  },
];

export default projectDashboardRoute;
